import { useCallback, useContext, useEffect, useRef, useState } from 'react';
import { ApiContext } from '@api/api';
import { CompanyListOutDTO } from '@api/ringil-api.v2';

/** One scroll's worth of companies. Small on purpose — the rest arrives as the list is scrolled. */
export const COMPANY_PAGE_SIZE = 50;

/**
 * The company list behind the admin pickers and filters, paged lazily.
 *
 * Admin-only by definition: `adminListCompanies` is the only endpoint that spans companies, and a
 * customer has exactly one, so for them there is nothing to choose between.
 *
 * `mode` decides how the pages are consumed. A picker the caller controls scrolls (`lazy`) and asks
 * for the next page as it reaches the end. The filter bar renders a plain option array it cannot
 * extend, so it fills itself page by page in the background (`all`) — same request size, just
 * without waiting for a scroll that will never be reported.
 */
export const useAdminCompanyOptions = (enabled: boolean, mode: 'lazy' | 'all' = 'lazy') => {
  const { adminListCompanies } = useContext(ApiContext);

  const [companies, setCompanies] = useState<CompanyListOutDTO[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  // Refs, not state: the loader is called from scroll handlers that must see the current values
  // without waiting for a re-render.
  const pageRef = useRef(0);
  const queryRef = useRef('');
  const loadingRef = useRef(false);
  const cancelledRef = useRef(false);

  const loadPage = useCallback(
    (pageNumber: number, query: string, append: boolean) => {
      if (loadingRef.current) return;
      loadingRef.current = true;
      setLoading(true);
      adminListCompanies(
        { pageSize: COMPANY_PAGE_SIZE, pageNumber },
        // `undefined`, never '': an empty string is sent as `q=` and applied as a filter.
        query || undefined,
        undefined,
        response => {
          loadingRef.current = false;
          setLoading(false);
          if (cancelledRef.current) return;
          pageRef.current = pageNumber;
          setTotal(response.total);
          setCompanies(previous => (append ? [...previous, ...response.data] : response.data));
          // The filter bar cannot ask for more, so keep going until the list is complete.
          if (mode === 'all' && append !== false && pageNumber * COMPANY_PAGE_SIZE < response.total && response.data.length > 0) {
            loadPage(pageNumber + 1, query, true);
          }
        },
        undefined,
        {
          // Without this the list silently keeps whatever it had, which reads as "no such company".
          onError: (_error, showStandardError) => {
            loadingRef.current = false;
            setLoading(false);
            showStandardError();
          },
        },
      );
    },
    [adminListCompanies, mode],
  );

  useEffect(() => {
    cancelledRef.current = false;
    if (!enabled) {
      setCompanies([]);
      setTotal(0);
      pageRef.current = 0;
      return;
    }
    loadPage(1, queryRef.current, mode === 'all');
    return () => {
      cancelledRef.current = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [enabled, mode]);

  /** Next page, for a picker that has been scrolled to the end of what it holds. */
  const loadMore = useCallback(() => {
    if (!enabled || loadingRef.current || companies.length >= total) return;
    loadPage(pageRef.current + 1, queryRef.current, true);
  }, [enabled, companies.length, total, loadPage]);

  /** Server-side search, so a company beyond the loaded pages is still findable by typing. */
  const search = useCallback(
    (query: string) => {
      if (!enabled || query === queryRef.current) return;
      queryRef.current = query;
      loadPage(1, query, false);
    },
    [enabled, loadPage],
  );

  return {
    companies,
    loading,
    hasMore: companies.length < total,
    loadMore,
    search,
    /** Shaped for `FilterConfig.options` / PrimeReact dropdowns, which need a string value. */
    options: companies.map(company => ({ label: company.name, value: company.companyId, active: true })),
  };
};